<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import axios from 'axios'
import SnappyJS from 'snappyjs'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getArticleList } from '@/api/http'
import { getHeadsetType, getSupportAreaCode, getSupportTitle } from '@/utils/common'
import { useInternationalStore } from '@/store/international'
import { isLegacyDetailLink } from '@/utils/supportLegacy'
import SupportArticleContent from './components/SupportArticleContent.vue'

interface ArticleItem {
  id: string | number
  title: string
  name?: string
  url?: string
}

interface BreadcrumbItem {
  text: string
  path?: string
  query?: Record<string, string>
}

const skeleton = ref(false)
const route = useRoute()
const router = useRouter()
const { locale } = useI18n()
const internationalStore = useInternationalStore()
const valueHtml = ref('')
const articleTitle = ref('')
const articleDescription = ref('')
const resolvedArticleUrl = ref('')
const resolvedArticleTitle = ref('')
const resolvedCategoryTitle = ref('')
const resolvedProductType = ref('')

const type = computed(() => route.params.type)
const articleId = computed(() => String(route.params.id || ''))
const valueUrl = computed(() => String(route.query.url || ''))
/*
 * 判断当前详情是否是旧 detail 链接
 * 旧 detail 链接以 query.url 为准展示旧样式；新 detail 通过 product + id 请求 v2
 */
const legacyDetail = computed(() => isLegacyDetailLink(route.query))
const routeTitle = computed(() => String(route.query.title || resolvedArticleTitle.value || ''))
const queryProductType = computed(() => String(route.query.product || ''))
const productType = computed(() => String(queryProductType.value || resolvedProductType.value || ''))
const productTitle = computed(() => String(route.query.productTitle || (productType.value ? getSupportTitle(productType.value) : '')))
const productPath = computed(() => String(route.query.productPath || (productType.value ? getSupportRoutePath(`/support/product/${productType.value}`) : '')))
const categoryTitle = computed(() => String(route.query.categoryTitle || resolvedCategoryTitle.value || ''))
const returnCategoryName = computed(() => String(route.query.categoryName || categoryTitle.value || ''))
const returnSectionType = computed(() => String(route.query.activeType || type.value || 'service_manual'))
/*
 * 获取返回分类页需要携带的查询参数
 * 产品页根据 activeType/categoryName 自动展开进入详情前的分类
 */
const getReturnCategoryQuery = () => {
  const query: Record<string, string> = {}
  if (returnSectionType.value) query.activeType = returnSectionType.value
  if (returnCategoryName.value) query.categoryName = returnCategoryName.value
  return query
}

const breadcrumbItems = computed(() => [
  productTitle.value ? { text: productTitle.value, path: productPath.value } : undefined,
  categoryTitle.value ? { text: categoryTitle.value, path: productPath.value || getSupportRoutePath('/support/index'), query: getReturnCategoryQuery() } : undefined,
  { text: articleTitle.value || routeTitle.value }
].filter(Boolean) as BreadcrumbItem[])
const articleLang = computed(() => {
  const lang = String(route.params.language || locale.value || import.meta.env.VITE_PAGE_LANGUAGE || 'en').toLowerCase()
  if (['jp', 'ja', 'jr'].includes(lang)) return 'ja'
  if (['kr', 'ko'].includes(lang)) return 'ko'
  return 'en'
})

const pushBreadcrumb = (item: BreadcrumbItem) => {
  if (!item.path) return
  router.push({
    path: item.path,
    query: item.query
  })
}

/*
 * 返回产品分类页
 * @description 新详情页点击返回时回到产品页，并携带分类展开参数
 */
const backToCategoryPage = () => {
  const fallbackPath = productPath.value || getSupportRoutePath('/support/index')

  router.push({
    path: fallbackPath,
    query: getReturnCategoryQuery()
  })
}

const getSupportRoutePath = (supportPath: string) => {
  const segments = route.path.split('/').filter(Boolean)
  const supportIndex = segments.findIndex((segment) => segment === 'support')
  const prefix = supportIndex > 0 ? `/${segments.slice(0, supportIndex).join('/')}` : ''
  return `${prefix}${supportPath}`
}

const isFullHtml = (value: string) => /^\s*(<!doctype|<html|<head|<body|<section|<article)\b/i.test(value)

/*
 * 获取并渲染文章内容
 * @param url - 文章内容地址
 * @param fallbackTitle - 接口或链接未提供标题时使用的兜底标题
 */
const getEditorData = (url: string, fallbackTitle = routeTitle.value) => {
  const timestamp = new Date().getTime()
  skeleton.value = true
  valueHtml.value = ''
  articleTitle.value = fallbackTitle
  articleDescription.value = ''

  if (!url) {
    skeleton.value = false
    return
  }

  axios({
    method: 'get',
    url: `${url}?t=${timestamp}`,
    responseType: 'text'
  })
    .then((res) => {
      const html = normalizeArticleResponse(res?.data)
      const article = pickLocalizedArticle(html, url)
      valueHtml.value = article.html
      articleTitle.value = article.title || fallbackTitle
      articleDescription.value = article.description
    })
    .catch(() => {
      valueHtml.value = ''
    })
    .finally(() => {
      skeleton.value = false
    })
}

/*
 * 标准化文章响应内容
 * @param data - 接口返回的文章原始内容
 */
const normalizeArticleResponse = (data: unknown) => {
  if (typeof data !== 'string') return ''
  if (isFullHtml(data)) return data
  try {
    return uncompressed(data)
  } catch (err) {
    return data
  }
}

/*
 * 获取当前语言对应的文章内容
 * @param html - 文章 HTML 字符串
 * @param sourceUrl - 文章来源地址，用于处理相对路径资源
 */
const pickLocalizedArticle = (html: string, sourceUrl: string) => {
  if (!html) return { html: '', title: routeTitle.value, description: '' }

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const lang = articleLang.value
  const section = doc.querySelector(`section[data-lang="${lang}"]`) || doc.querySelector('section[data-lang="en"]') || doc.body
  const title = doc.querySelector(`meta[name="support:title:${lang}"]`)?.getAttribute('content') || section.querySelector('h1')?.textContent?.trim() || routeTitle.value
  const description = doc.querySelector(`meta[name="support:description:${lang}"]`)?.getAttribute('content') || section.querySelector('.lede')?.textContent?.trim() || ''
  const contentClone = section.cloneNode(true) as HTMLElement
  const styleHtml = getArticleStyleHtml(doc, sourceUrl)

  contentClone.querySelectorAll('script').forEach((item) => item.remove())
  resolveRelativeAssets(contentClone, sourceUrl)

  return {
    html: `${styleHtml}${section === doc.body ? contentClone.innerHTML : contentClone.outerHTML}`,
    title,
    description
  }
}

/*
 * 获取文章 HTML 中自带的样式
 * @description 后端 HTML 自带 style/link 时需要保留，否则 v-html 只渲染正文会丢失样式
 * @param doc - 文章 HTML 解析后的文档
 * @param sourceUrl - 文章来源地址
 */
const getArticleStyleHtml = (doc: Document, sourceUrl: string) => {
  const styleNodes = Array.from(doc.querySelectorAll('style, link[rel~="stylesheet"]'))
  return styleNodes.map((node) => {
    const clone = node.cloneNode(true) as HTMLElement
    const tagName = clone.tagName.toLowerCase()
    if (tagName === 'style') {
      clone.textContent = normalizeArticleCss(clone.textContent || '', sourceUrl)
    }
    if (tagName === 'link') {
      const href = clone.getAttribute('href')
      if (href) clone.setAttribute('href', resolveUrl(href, sourceUrl))
    }
    return clone.outerHTML
  }).join('')
}

/*
 * 标准化文章 CSS
 * @description 后端 HTML 以浏览器默认 16px rem 为基准，当前项目 html 字号为 10px，需要转成 px 保持原尺寸
 * @param css - 后端 HTML 中的 CSS 文本
 * @param sourceUrl - 文章来源地址
 */
const normalizeArticleCss = (css: string, sourceUrl: string) => {
  return convertArticleRemToPx(scopeArticleCss(normalizeArticleTypographyCss(resolveCssUrls(css, sourceUrl))))
}

/*
 * 关闭文章 HTML 中会触发 fi 连字的文字渲染优化
 * @param css - 后端 HTML 中的 CSS 文本
 */
const normalizeArticleTypographyCss = (css: string) => {
  return css.replace(/text-rendering\s*:\s*optimizeLegibility\s*;?/gi, 'text-rendering: auto;')
}

/*
 * 将文章 CSS 中的 rem 按独立 HTML 默认基准转成 px
 * @param css - 后端 HTML 中的 CSS 文本
 */
const convertArticleRemToPx = (css: string) => {
  return css.replace(/(-?\d*\.?\d+)rem\b/g, (_match, value) => {
    return `${Number(value) * 16}px`
  })
}

/*
 * 将文章 CSS 的页面根选择器映射到 Shadow DOM 宿主
 * @param css - 后端 HTML 中的 CSS 文本
 */
const scopeArticleCss = (css: string) => {
  return css
    .replace(/:root\b/g, ':host')
    .replace(/(^|[,{]\s*)html(?=\s|\{|,|\.|#|:|\[|>|\+|~)/g, '$1:host')
    .replace(/(^|[,{]\s*)body(?=\s|\{|,|\.|#|:|\[|>|\+|~)/g, '$1:host')
}

/*
 * 修正 CSS 中的相对 url 资源
 * @param css - 后端 HTML 中的 CSS 文本
 * @param sourceUrl - 文章来源地址
 */
const resolveCssUrls = (css: string, sourceUrl: string) => {
  return css.replace(/url\((['"]?)(?!data:|https?:|\/\/|#)([^'")]+)\1\)/g, (_match, quote, value) => {
    return `url(${quote}${resolveUrl(value, sourceUrl)}${quote})`
  })
}

/*
 * 修正文章内相对路径资源
 * @param root - 待处理的文章根节点
 * @param sourceUrl - 文章来源地址
 */
const resolveRelativeAssets = (root: HTMLElement, sourceUrl: string) => {
  root.querySelectorAll('[src]').forEach((item) => {
    const src = item.getAttribute('src')
    if (src) item.setAttribute('src', resolveUrl(src, sourceUrl))
  })
  root.querySelectorAll('[poster]').forEach((item) => {
    const poster = item.getAttribute('poster')
    if (poster) item.setAttribute('poster', resolveUrl(poster, sourceUrl))
  })
  root.querySelectorAll('a[href]').forEach((item) => {
    const href = item.getAttribute('href')
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return
    item.setAttribute('href', resolveUrl(href, sourceUrl))
    item.setAttribute('target', '_blank')
    item.setAttribute('rel', 'noopener noreferrer')
  })
}

/*
 * 解析文章资源地址
 * @param value - 原始资源地址
 * @param sourceUrl - 文章来源地址
 */
const resolveUrl = (value: string, sourceUrl: string) => {
  try {
    return new URL(value, sourceUrl).href
  } catch (err) {
    return value
  }
}

/*
 * 从服务支持配置中查找当前文章
 * @param supportList - 服务支持接口返回的分类列表
 * @param fallbackProductType - 未从接口分类中取到产品时的兜底产品类型
 */
const findArticleFromSupportList = (supportList: any[] = [], fallbackProductType = productType.value) => {
  const sectionType = String(type.value || '')
  const sections = sectionType
    ? supportList.filter((section) => section.type === sectionType)
    : supportList.filter((section) => ['service_manual', 'troubleshooting'].includes(section.type))

  for (const section of sections) {
    const article = (section.info_list || []).find((item: ArticleItem) => String(item.id) === articleId.value)
    if (article) {
      return {
        article,
        categoryTitle: article.name || '',
        productType: getProductTypeFromSupportSection(section, fallbackProductType),
        sectionType: section.type
      }
    }
  }

  return undefined
}

/*
 * 获取文章所属产品类型
 * @param section - 服务支持分类数据
 * @param fallbackProductType - 兜底产品类型
 */
const getProductTypeFromSupportSection = (section: any, fallbackProductType = productType.value) => {
  return fallbackProductType || section?.headset_model || ''
}

/*
 * 从新服务支持接口加载详情文章
 */
const loadArticleFromConfig = () => {
  const headsetModel = getHeadsetType(productType.value)

  if (!articleId.value || !headsetModel) {
    getEditorData(valueUrl.value)
    return
  }

  skeleton.value = true
  valueHtml.value = ''
  articleTitle.value = routeTitle.value
  articleDescription.value = ''

  getArticleList({
    headset_model: headsetModel,
    area_code: getSupportAreaCode(internationalStore.location.abb, route.path)
  })
    .then((res: any) => {
      const detail = findArticleFromSupportList(res.support_list || [], productType.value)
      if (!detail?.article?.url) {
        getEditorData(valueUrl.value)
        return
      }
      resolvedArticleUrl.value = detail.article.url
      resolvedArticleTitle.value = detail.article.title
      resolvedCategoryTitle.value = detail.categoryTitle
      resolvedProductType.value = productType.value || detail.productType
      getEditorData(detail.article.url, detail.article.title)
    })
    .finally(() => {
      if (!resolvedArticleUrl.value) skeleton.value = false
    })
}

/*
 * 加载详情文章
 * @description 旧 detail 使用链接中的 url，新 detail 使用 product/id/type 到 v2 查询
 */
const loadDetailArticle = () => {
  resolvedArticleUrl.value = ''
  resolvedArticleTitle.value = ''
  resolvedCategoryTitle.value = ''
  resolvedProductType.value = ''

  if (valueUrl.value) {
    /*
     * 旧 detail 链接直接使用 URL 渲染内容
     * 不再尝试用旧 id 匹配新接口
     */
    getEditorData(valueUrl.value)
    return
  }

  /*
   * 新 detail 链接只依赖当前 product/id/type
   * 到 v2 接口中查文章
   */
  loadArticleFromConfig()
}

// 解压
const uncompressed = (data: string) => {
  const values = atob(data).split(',')
  const len = values.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    bytes[i] = parseInt(values[i], 10)
  }
  const decoder = new TextDecoder()
  const arrayBuffer = SnappyJS.uncompress(bytes)
  return decoder.decode(arrayBuffer)
}

watch(
  () => [valueUrl.value, articleLang.value, type.value, articleId.value, queryProductType.value],
  () => {
    if (type.value !== 'video_tutorial') loadDetailArticle()
  },
  { immediate: true }
)
</script>

<template>
  <main class="mobileDetail" v-if="legacyDetail">
    <div class="mobileDetail__content">
      <el-skeleton animated class="select" :loading="skeleton">
        <template #template>
          <el-skeleton-item variant="h1" class="legacy-skeleton-h2" />
          <el-skeleton-item variant="h3" class="legacy-skeleton-h3" />
          <el-skeleton-item variant="rect" v-for="item in 4" :key="`legacy-a-${item}`" />
          <el-skeleton-item variant="rect" style="width: 60%" />
          <el-skeleton-item variant="h3" class="legacy-skeleton-h3" />
          <el-skeleton-item variant="rect" v-for="item in 8" :key="`legacy-b-${item}`" />
          <el-skeleton-item variant="rect" style="width: 60%" />
        </template>
        <template #default>
          <h2>{{ routeTitle }}</h2>
          <SupportArticleContent class="wangEditor-mobile" v-if="valueHtml" :html="valueHtml" legacy />
          <div class="mobileDetail__empty" v-else>{{ $t('Support.product.articleUnavailable') }}</div>
        </template>
      </el-skeleton>
    </div>
  </main>
  <main class="support-detail-page" v-else>
    <div class="support-detail-page__breadcrumb-wrap">
      <ul class="support-detail-page__breadcrumb">
        <li class="pointer" @click="router.push('/support/index')">{{ $t('Support.product.home') }}</li>
        <template v-for="(item, index) in breadcrumbItems" :key="`${item.text}-${index}`">
          <li class="support-detail-page__breadcrumb-separator">></li>
          <li :class="{ pointer: item.path }" @click="pushBreadcrumb(item)">{{ item.text }}</li>
        </template>
      </ul>
    </div>

    <section class="support-detail-page__hero">
      <button class="support-detail-page__back" type="button" @click="backToCategoryPage">
        <img src="https://viaim-h5.oss-ap-southeast-1.aliyuncs.com/iflybuds-website-world/images/support/back.png" alt="" />
        <span class="support-detail-page__title">{{ $t('Support.product.back') }}</span>
      </button>
    </section>
    <div class="support-detail-page__divider"></div>

    <section class="support-detail-page__shell">
      <el-skeleton animated class="support-detail-page__skeleton" :loading="skeleton">
        <template #template>
          <el-skeleton-item variant="h1" class="skeleton-title" />
          <el-skeleton-item variant="text" class="skeleton-lede" />
          <el-skeleton-item variant="rect" class="skeleton-summary" />
          <el-skeleton-item variant="h3" class="skeleton-heading" />
          <el-skeleton-item variant="text" v-for="item in 5" :key="`a-${item}`" />
          <el-skeleton-item variant="h3" class="skeleton-heading" />
          <el-skeleton-item variant="text" v-for="item in 7" :key="`b-${item}`" />
        </template>
        <template #default>
          <article class="support-article">
            <header class="support-article__header" v-if="!valueHtml">
              <h1>{{ articleTitle || routeTitle }}</h1>
              <p v-if="articleDescription">{{ articleDescription }}</p>
            </header>
            <SupportArticleContent class="support-article__content" v-if="valueHtml" :html="valueHtml" />
          </article>
        </template>
      </el-skeleton>
    </section>
  </main>
</template>

<style lang="scss" scoped>
.mobileDetail {
  width: 100%;
  min-height: calc(100vh - 16rem);
  padding: 1.6rem;
  background: #fff;

  &__content {
    width: 100%;
    max-width: 96rem;
    margin: 0 auto;
    box-sizing: border-box;
  }

  .legacy-skeleton-h2 {
    width: 9.3rem;
    height: 3.4rem;
  }

  .legacy-skeleton-h3 {
    margin: 2.4rem 0 1rem 0;
    width: 15rem;
    height: 2.5rem;
    display: block;
  }

  h2 {
    margin: 0 0 1.6rem;
    color: #1a1a1a;
    font-size: 2rem;
    font-weight: 700;
    line-height: 1.4;
  }

  .wangEditor-mobile :deep(h1) {
    font-size: 20px;
  }

  .wangEditor-mobile :deep(h2),
  .wangEditor-mobile :deep(h3) {
    font-size: 15px;
    font-weight: normal;
  }

  .wangEditor-mobile :deep(h4) {
    font-size: 13px;
    font-weight: normal;
  }

  .wangEditor-mobile :deep(h5) {
    font-size: 16px;
    font-weight: normal;
  }

  .wangEditor-mobile :deep(p) {
    line-height: 1.8;
  }

  .wangEditor-mobile :deep(a) {
    color: #1877f2;
  }

  .wangEditor-mobile,
  .wangEditor-mobile :deep(*) {
    font-variant-ligatures: none;
    font-feature-settings: "liga" 0, "clig" 0;
    text-rendering: auto;
  }

  .wangEditor-mobile :deep([id]),
  .wangEditor-mobile :deep(a[name]) {
    scroll-margin-top: 8rem;
  }
}

.mobileDetail__empty {
  padding: 4rem 0;
  color: #989898;
  font-size: 1.4rem;
  line-height: 2rem;
  text-align: center;
}

.support-detail-page {
  width: 100%;
  min-height: calc(100vh - 16rem);
  background: #fff;
}

.support-detail-page__breadcrumb-wrap {
  width: 100%;
  background: #f2f2f2;
}

.support-detail-page__breadcrumb {
  width: 100%;
  max-width: 96rem;
  height: 5.6rem;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: #575757;
  font-family: 'PingFang SC';
  font-size: 1.5rem;
  font-weight: 400;
  line-height: 2rem;

  .pointer {
    cursor: pointer;
  }

  li {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: 'PingFang SC';
    font-size: inherit;
    font-weight: 400;
    line-height: inherit;
  }

  li:not(.support-detail-page__breadcrumb-separator):not(:last-child) {
    flex-shrink: 0;
    max-width: 24rem;
  }

  li:last-child {
    flex: 1;
  }

  .support-detail-page__breadcrumb-separator {
    flex: none;
    max-width: none;
  }
}

.support-detail-page__hero {
  width: 100%;
  max-width: 96rem;
  margin: 0 auto;
  padding-top: 4rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.8rem;
  box-sizing: border-box;

}

.support-detail-page__title {
  color: #202020;
  font-size: 1.6rem;
  font-weight: 500;
  line-height: 2.4rem;
  text-align: left;
}

.support-detail-page__back {
  padding: 0;
  border: 0;
  background: transparent;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.8rem;
  cursor: pointer;

  img {
    width: 2.4rem;
    height: 2.4rem;
    display: block;
  }
}

.support-detail-page__divider {
  width: 100%;
  max-width: 96rem;
  height: 0.1rem;
  margin: 2.4rem auto 0;
  background: #d9d9d9;
}

.support-detail-page__shell {
  width: 100%;
  max-width: 96rem;
  margin: 0 auto;
  padding: 0;
}

.support-detail-page__skeleton,
.support-article {
  width: 100%;
  margin: 0;
  box-sizing: border-box;
}

.support-article {
  min-height: 24rem;
  padding: 0;
  border-radius: 1rem;
}

.skeleton-title {
  width: 68%;
  height: 4.8rem;
}

.skeleton-lede {
  width: 86%;
  height: 2.4rem;
  margin-top: 1.6rem;
}

.skeleton-summary {
  width: 100%;
  height: 8.8rem;
  margin: 3.2rem 0;
  border-radius: 1rem;
}

.skeleton-heading {
  width: 42%;
  height: 3.2rem;
  margin: 4.8rem 0 1.6rem;
}

.support-article__header {
  margin-bottom: 3.2rem;

  h1 {
    margin: 0;
    color: #1a1a1a;
    font-size: 3.6rem;
    font-weight: 700;
    line-height: 1.3;
  }

  p {
    margin: 1.6rem 0 0;
    color: #6b7280;
    font-size: 1.8rem;
    line-height: 1.6;
  }
}

.support-article__content {
  -webkit-font-smoothing: antialiased;
  font-variant-ligatures: none;
  font-feature-settings: "liga" 0, "clig" 0;
  text-rendering: auto;

  :deep(*) {
    font-variant-ligatures: none;
    font-feature-settings: "liga" 0, "clig" 0;
    text-rendering: auto;
  }

  :deep([id]),
  :deep(a[name]) {
    scroll-margin-top: 8rem;
  }
}

@media screen and (min-width: 750px) {

  .support-detail-page__breadcrumb,
  .support-detail-page__hero,
  .support-detail-page__divider,
  .support-detail-page__shell {
    width: calc(100% - 4.8rem);
    max-width: 96rem;
    margin-left: auto;
    margin-right: auto;
  }
}

@media screen and (max-width: 749px) {
  .support-detail-page {
    min-height: calc(100vh - 16.4rem);
  }

  .support-detail-page__breadcrumb {
    height: 5.6rem;
    padding: 0 1.6rem;
    box-sizing: border-box;
    gap: 1.2rem;
    font-size: 1.2rem;

    li:not(.support-detail-page__breadcrumb-separator):not(:last-child) {
      max-width: 8.8rem;
    }
  }

  .support-detail-page__hero {
    width: auto;
    max-width: none;
    margin: 0 1.6rem;
    padding: 1.6rem 0 0;
    gap: 1.2rem;

    .support-detail-page__title {
      color: #1a1a1a;
    }
  }

  .support-detail-page__back {
    gap: 1.2rem;

    img {
      width: 2rem;
      height: 2rem;
    }
  }

  .support-detail-page__divider {
    width: auto;
    max-width: none;
    margin: 2.4rem 1.6rem 0;
  }

  .support-detail-page__shell {
    width: auto;
    max-width: none;
    margin: 0 1.6rem;
    padding: 0;
    box-sizing: border-box;
  }

  .support-detail-page__skeleton {
    width: 100%;
    padding: 2.4rem 1.6rem;
    border-radius: 0.8rem;
  }

  .support-article {
    width: 100%;
    padding: 0;
    border-radius: 0.8rem;
  }

}
</style>
